import { type JSX, useState } from "react";

import { ContextBar } from "./components/ContextBar";
import { HealthPanel } from "./components/HealthPanel";
import { IdentityPanel } from "./components/IdentityPanel";
import { MAIN_TABS, type MainTab, OperationsScreen } from "./components/OperationsScreen";
import { ScopeEditor } from "./components/ScopeEditor";
import { useBench } from "./hooks/useBench";
import { useCatalog } from "./hooks/useCatalog";
import { useProjects } from "./hooks/useProjects";

export function App(): JSX.Element {
  // Which of the three things the page is for. Held here because it decides both the pane below
  // and whether the session-context panels are worth the space.
  const [tab, setTab] = useState<MainTab>("overview");
  // Which deployment to sign in to, when more than one is configured.
  const [chosen, setChosen] = useState<string | null>(null);
  const bench = useBench();
  // Re-read the catalogue whenever the deployment or identity changes: a different
  // deployment can expose a different API surface.
  const sessionKey = bench.session
    ? `${bench.session.environment}:${bench.session.profile}`
    : null;
  const catalog = useCatalog(sessionKey);
  // The project picker's list depends on the same two things plus the tenant, which is only
  // editable where auth is off. Saving a project or a working session must not reload it.
  const tenantKey = bench.session ? `${sessionKey}:${bench.session.scope.tenant_id}` : null;
  const projects = useProjects(tenantKey);

  if (bench.boot.kind === "loading") {
    return <main className="page page--centred">Loading the test bench…</main>;
  }

  if (bench.boot.kind === "error") {
    return (
      <main className="page page--centred">
        <div className="notice notice--error">
          <h1>The test bench could not start</h1>
          <p>{bench.boot.message}</p>
        </div>
      </main>
    );
  }

  const { environments, profiles, bench: benchHealth } = bench.boot;

  if (environments.length === 0) {
    return (
      <main className="page page--centred">
        <div className="notice notice--error">
          <h1>No environments configured</h1>
          <p>
            Set <code>BENCH_ENVIRONMENTS</code> to a JSON array of environment objects, then
            restart the bench.
          </p>
        </div>
      </main>
    );
  }

  const { session } = bench;
  const active =
    environments.find((env) => env.name === (session?.environment ?? chosen)) ??
    environments[0]!;

  if (!session) {
    // No start page. Where nobody signs in a session is created on load, so the only reason to be
    // here is a deployment that does -- and then the page is the way in and nothing else. Not even
    // the overview: it reads the tester's own usage, so without a token there is nothing to show.
    return (
      <>
        <ContextBar
          renewalFailed={false}
          tester={null}
          signOutHref={null}
          benchVersion={benchHealth.version}
        />
        <main className="page">
          {/* No tab bar: every tab needs a token, so a lone tab would only be scenery. */}
          {bench.actionError && (
            <div className="notice notice--error">
              <strong>That did not work.</strong> {bench.actionError}
            </div>
          )}

          {active.auth_mode === "oidc" ? (
          <section className="panel">
            <h2>Sign in to continue</h2>

            {environments.length > 1 && (
              <label className="form-grid">
                <span>Environment</span>
                <select
                  value={chosen ?? active.name}
                  onChange={(event) => setChosen(event.target.value)}
                >
                  {environments.map((env) => (
                    <option key={env.name} value={env.name}>
                      {env.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="panel__actions">
              <a
                className="button primary"
                href={`/api/auth/login?environment_name=${encodeURIComponent(active.name)}`}
              >
                Sign In
              </a>
            </div>
          </section>
          ) : (
            // Nothing to sign in to here, so being session-less means the automatic one failed.
            // Say what went wrong and offer the retry, rather than a sign-in that does not exist.
            <section className="panel">
              <h2>{bench.busy ? "Starting a session…" : "No session"}</h2>
              <p className="muted">
                {active.label} mints its own tokens, so the bench starts a session by itself. That
                did not work this time.
              </p>
              <div className="panel__actions">
                <button
                  type="button"
                  className="primary"
                  disabled={bench.busy || !profiles[0]}
                  onClick={() => {
                    const profile = profiles[0];
                    if (profile) {
                      void bench.connect(active.name, profile);
                    }
                  }}
                >
                  Try again
                </button>
              </div>
            </section>
          )}
        </main>
      </>
    );
  }

  return (
    <>
      <ContextBar
        renewalFailed={bench.renewalFailed}
        tester={session.tester}
        signOutHref={session.tester ? "/api/auth/logout" : null}
        benchVersion={benchHealth.version}
      />

      <main className="page">
        {/*
          Page navigation, so it comes before anything it navigates. It used to sit below the
          scope, identity and health panels, which meant scrolling past three cards to change
          what the page was for.
        */}
        <nav className="tabs tabs--main" role="tablist">
          {MAIN_TABS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              role="tab"
              aria-selected={tab === entry.key}
              title={entry.hint}
              className={tab === entry.key ? "tabs__tab tabs__tab--active" : "tabs__tab"}
              onClick={() => setTab(entry.key)}
            >
              {entry.label}
            </button>
          ))}
        </nav>

        {bench.actionError && (
          <div className="notice notice--error">
            <strong>That did not work.</strong> {bench.actionError}
          </div>
        )}

        {!active.auth_enabled && (
          <div className="notice notice--warning">
            <strong>{active.label} runs with authentication disabled.</strong> Scope comes from
            headers alone, and the Memory Service skips its <code>memory-admin</code> check on
            project-memory reset — there is no identity to carry a role — so that destructive
            endpoint is ungated here.
          </div>
        )}

        {/*
          Session setup lives on one tab: what scope calls carry, which identity makes them, and
          whether the service is even up. Grouped here rather than repeated above every screen --
          they are things you set once and then go and use.
        */}
        {tab === "setup" && (
          <>
        <div className="columns">
          <ScopeEditor
            scope={session.scope}
            environment={active}
            projectOptions={active.default_projects}
            projects={projects.state}
            onApply={(patch) => void bench.applyScope(patch)}
            busy={bench.busy}
          />
          {/* Only where the deployment mints the seeded profiles. Signing in says who is at the
              keyboard; this says whose token the next call carries, and being able to send the
              wrong one on purpose is the whole point -- but that only exists where POST /v1/token
              does. Everywhere else there is exactly one identity, the signed-in tester's own, and
              the panel degenerated into three red "does not mint tokens" cards reporting a route
              that is absent by design. The probe still runs and its result is still in the session
              payload; this hides a switcher with nothing to switch to. */}
          {active.dev_token_expected && (
            <IdentityPanel
              session={session}
              onSelect={(profile) => void bench.selectProfile(profile)}
              busy={bench.busy}
            />
          )}
        </div>

        <HealthPanel
          health={bench.amsHealth?.health ?? null}
          gitea={bench.amsHealth?.gitea ?? null}
          loading={bench.healthLoading}
          onRefresh={() => void bench.checkHealth(active.name)}
        />
          </>
        )}

        <OperationsScreen
          environment={active}
          session={session}
          catalog={catalog}
          tab={tab}
        />
      </main>
    </>
  );
}
